import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  SHOPIFY_READY,
  isCatalogLive,
  remoteCartCreate,
  remoteCartGet,
  remoteCartLinesAdd,
  remoteCartLinesUpdate,
  remoteCartLinesRemove,
} from '../lib/shopify';
import { track } from '../lib/fbpixel';

const CartContext = createContext(null);
const STORAGE_KEY = 'vizio_cart_v1';
const CART_REF_KEY = 'vizio_shopify_cart_ref';

function loadCartRef() {
  try {
    return JSON.parse(localStorage.getItem(CART_REF_KEY)) || {};
  } catch {
    return {};
  }
}

export function CartProvider({ children }) {
  const [lines, setLines] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [isOpen, setOpen] = useState(false);
  const [cartId, setCartId] = useState(() =>
    SHOPIFY_READY ? loadCartRef().id || null : null
  );
  const [checkoutUrl, setCheckoutUrl] = useState(() =>
    SHOPIFY_READY ? loadCartRef().url || null : null
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  }, [lines]);

  useEffect(() => {
    if (!SHOPIFY_READY) return;
    if (cartId) {
      localStorage.setItem(CART_REF_KEY, JSON.stringify({ id: cartId, url: checkoutUrl }));
    } else {
      localStorage.removeItem(CART_REF_KEY);
    }
  }, [cartId, checkoutUrl]);

  const applyRemote = useCallback((remote) => {
    setCartId(remote.cartId);
    setCheckoutUrl(remote.checkoutUrl);
    setLines(remote.lines);
  }, []);

  // Re-sync the persisted Shopify cart on load (Shopify is the source of truth)
  useEffect(() => {
    if (!SHOPIFY_READY || !cartId) return;
    let alive = true;
    remoteCartGet(cartId)
      .then((remote) => {
        if (!alive) return;
        if (remote) {
          applyRemote(remote);
        } else {
          setCartId(null);
          setCheckoutUrl(null);
          setLines([]);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportError = useCallback((e) => {
    toast('SHOPIFY', { description: String(e?.message || e) });
  }, []);

  const addLine = useCallback(
    async (product, variant, quantity = 1) => {
      let ok = true;
      if (isCatalogLive()) {
        setBusy(true);
        try {
          const payload = [{ merchandiseId: variant.id, quantity }];
          const remote = cartId
            ? await remoteCartLinesAdd(cartId, payload)
            : await remoteCartCreate(payload);
          applyRemote(remote);
        } catch (e) {
          ok = false;
          reportError(e);
        } finally {
          setBusy(false);
        }
      } else {
        setLines((prev) => {
          const found = prev.find((l) => l.key === variant.id);
          if (found) {
            return prev.map((l) =>
              l.key === variant.id ? { ...l, quantity: l.quantity + quantity } : l
            );
          }
          return [
            ...prev,
            {
              key: variant.id,
              quantity,
              variant: {
                id: variant.id,
                title: variant.title,
                sku: variant.sku,
                price: variant.price,
              },
              product: {
                title: product.title,
                handle: product.handle,
                image: product.images?.[0]?.url || null,
              },
            },
          ];
        });
      }
      if (ok) {
        setOpen(true);
        // Meta Pixel — AddToCart (single choke point for every add-to-cart button)
        track('AddToCart', {
          content_type: 'product',
          content_ids: [variant?.sku || variant?.id || product?.handle].filter(Boolean),
          content_name: product?.title,
          contents: [{ id: variant?.sku || variant?.id, quantity }],
          value: Number(variant?.price || 0) * quantity,
          currency: 'EUR',
        });
      }
    },
    [cartId, applyRemote, reportError]
  );

  const updateQuantity = useCallback(
    async (lineKey, quantity) => {
      if (quantity < 1) return;
      if (isCatalogLive() && cartId) {
        setBusy(true);
        try {
          applyRemote(await remoteCartLinesUpdate(cartId, [{ id: lineKey, quantity }]));
        } catch (e) {
          reportError(e);
        } finally {
          setBusy(false);
        }
      } else {
        setLines((prev) =>
          prev.map((l) => (l.key === lineKey ? { ...l, quantity } : l))
        );
      }
    },
    [cartId, applyRemote, reportError]
  );

  const removeLine = useCallback(
    async (lineKey) => {
      if (isCatalogLive() && cartId) {
        setBusy(true);
        try {
          applyRemote(await remoteCartLinesRemove(cartId, [lineKey]));
        } catch (e) {
          reportError(e);
        } finally {
          setBusy(false);
        }
      } else {
        setLines((prev) => prev.filter((l) => l.key !== lineKey));
      }
    },
    [cartId, applyRemote, reportError]
  );

  const checkout = useCallback(() => {
    // Meta Pixel — InitiateCheckout (fires as the user proceeds to Shopify checkout)
    track('InitiateCheckout', {
      content_type: 'product',
      content_ids: lines.map((l) => l.variant.sku || l.variant.id).filter(Boolean),
      num_items: lines.reduce((s, l) => s + l.quantity, 0),
      value: lines.reduce((s, l) => s + Number(l.variant.price) * l.quantity, 0),
      currency: 'EUR',
    });
    if (SHOPIFY_READY && checkoutUrl) {
      window.location.href = checkoutUrl;
      return;
    }
    toast('SHOPIFY CHECKOUT', {
      description:
        'Store connected. Checkout goes live automatically as soon as the 01 / CHROME product exists on the Shopify store (handle: 01-chrome).',
    });
  }, [checkoutUrl, lines]);

  const count = useMemo(() => lines.reduce((s, l) => s + l.quantity, 0), [lines]);
  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + Number(l.variant.price) * l.quantity, 0),
    [lines]
  );

  const value = {
    lines,
    count,
    subtotal,
    isOpen,
    setOpen,
    addLine,
    updateQuantity,
    removeLine,
    checkout,
    busy,
    shopifyReady: SHOPIFY_READY,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
